import { NavLink, useNavigate } from "react-router-dom";
import { Bot, MessageSquare, Settings, UserCircle, LogOut } from "lucide-react";
import { cn } from "@app/lib/utils";
import { logout, type MeResponse } from "@app/lib/api";
import { Button } from "@app/components/ui/button";

interface AppShellProps {
  me: MeResponse;
  children: React.ReactNode;
  statusLabel?: string;
  statusTone?: "ok" | "warn" | "down" | "idle";
  onLogout?: () => void;
}

const navItems = [
  { to: "/", label: "Agent", icon: Bot, end: true },
  { to: "/chat", label: "Chat", icon: MessageSquare, end: false },
  { to: "/settings", label: "Settings", icon: Settings, end: false },
  { to: "/account", label: "Account", icon: UserCircle, end: false },
];

export function AppShell({
  me,
  children,
  statusLabel = "Idle",
  statusTone = "idle",
  onLogout,
}: AppShellProps) {
  const nav = useNavigate();
  const onSignOut = async () => {
    try {
      await logout();
    } finally {
      onLogout?.();
      nav("/login", { replace: true });
    }
  };
  const toneClass = {
    ok: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    warn: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    down: "bg-danger/15 text-danger border-danger/30",
    idle: "bg-muted text-muted-fg border-border",
  }[statusTone];

  return (
    <div className="flex min-h-full bg-bg text-fg">
      <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-muted/30 p-4">
        <div className="px-2 pb-4">
          <div className="flex items-center gap-2">
            <div className="text-base font-semibold">John CEO</div>
            <span
              title="This deployment is a preview. Billing and credits are not active."
              className="rounded-sm border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300"
            >
              Preview
            </span>
          </div>
          <div className="text-xs text-muted-fg">{me.email}</div>
        </div>
        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-accent text-accent-fg" : "text-fg hover:bg-muted",
                )
              }
            >
              <Icon className="h-4 w-4" aria-hidden />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3">
          <div
            className={cn(
              "flex items-center justify-between rounded-md border px-3 py-2 text-xs",
              toneClass,
            )}
          >
            <span className="font-medium">Status</span>
            <span>{statusLabel}</span>
          </div>
          <Button variant="outline" size="sm" onClick={onSignOut} className="justify-start">
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-8">{children}</main>
    </div>
  );
}
